import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getPageLocation } from '@nykaa/listing-shared/utils';
import { scaleImageInUri } from '@nykaa/utils/image';
import ProductCard from '@nykaa/product-card-desktop';
import { fetchVariants } from '@nykaa/product-card-shared/store/variants/action';
import { State } from 'store/types';
import { isRegisteredViaMobile } from 'store/auth/helper';
import { toShowProTag } from 'helpers/utils/product/toShowProTag';
import { redirectAuthPage } from 'helpers/auth';
import ActionWrapper from 'components/CardAction';
import { ProductCardProps, ProductRenderProps } from 'types/product';
import { LAZYLOAD_COUNT } from 'constants/product';
import { syncImpressionsHandler } from 'shared/helpers/impressions/syncImpressionsHandler';
import { IMPRESSION_TYPE } from 'shared/helpers/impressions/constants';
import { checkIsIndianUser } from 'helpers/utils/user';
import {
  generateProductImpressionData,
  generateProductClickedData,
} from '@nykaa/utils/retina/helpers';
import { sendSingleEvent } from 'shared/helpers/impressions/sendSingleRetinaEvent';
import { pushEvent } from '@nykaa/data-layer';
import { PRODUCT_CARD_ACTION } from './constants';
import { useIsDifferentialPricingEnabled } from 'hooks/priceReveal/differentialPrice';
import useShouldDisableCta from 'hooks/Product/action';

const Product = ({
  product,
  isPro,
  positionInList,
  size = 'compact',
  customClass = '',
  hasInteracted,
  journeyData,
  setInteraction,
  tagsOnListingEnabled,
  tagsV2Enabled,
}: ProductCardProps) => {
  const dispatch = useDispatch();
  const variants = useSelector((state: State) => state.variants);
  const regionCountry = useSelector(
    (state: State) => state.appReducer?.region?.regionHeaderCountry
  );
  const history = useHistory();
  const suggestionType = useSelector(
    (state: State) => state.dataLayer.listingPageType
  );
  const listingOpenedFrom = useSelector(
    (state: State) => state.dataLayer?.listingOpenedFrom || ''
  );
  const disableCta = useShouldDisableCta();

  const categoryId = useSelector(
    (state: State) => state.dataLayer.category?.id
  );
  const plpPriceReveal = useSelector(
    (state: State) => state.remoteConfig.configs.plpPriceReveal
  );
  const listingPriceReveal = useSelector(
    (state: State) => state.remoteConfig.configs.listingPriceReveal
  );
  const isNavParamsEnabled = useSelector(
    (state: State) => state.remoteConfig.switches.isNavParamsEnabled
  );
  const { search: queryString } = history.location;
  const queryParams = new URLSearchParams(queryString);
  let root = queryParams.get('root');
  const offerRoot = queryParams.get('offer_root');
  const isDifferentialPricingEnabled = useIsDifferentialPricingEnabled();
  const siteNavigation = queryParams?.get('site_navigation') || '';
  const siteSubNavigation = queryParams?.get('site_sub_navigation') || '';
  if (offerRoot) {
    root = offerRoot;
  }
  const fetchVariantsAction = useCallback(
    async (params) => {
      await dispatch(fetchVariants(params));
    },
    [dispatch]
  );
  const user = useSelector((state: State) => state.auth.user);
  const { imageUrl, proFlag } = product;

  const showProTag = toShowProTag(isPro, proFlag);
  const pageLocation = getPageLocation({ root, categoryId, suggestionType });
  const optimizedImage =
    imageUrl && scaleImageInUri({ url: imageUrl, width: 220, height: 220 });
  const shouldShowDefaultColor = __PLATFORM__ === 'men';
  const priceRevealCatId = plpPriceReveal?.categoryId || [];
  const updatedText = plpPriceReveal?.text?.trim();
  const isRegionIndia = checkIsIndianUser(regionCountry);
  if (
    plpPriceReveal?.status &&
    (priceRevealCatId.indexOf(categoryId) > -1 || !updatedText)
  ) {
    plpPriceReveal.status = false;
  }
  const productTrackingData = {
    product,
    additionalProductParams: {
      productPosition: positionInList,
    },
  };
  const onIntersect = () => {
    productTrackingData &&
      syncImpressionsHandler(
        [generateProductImpressionData(productTrackingData)],
        IMPRESSION_TYPE.PRODUCT,
        isRegionIndia
      );
  };

  const handleProductClick = () => {
    sendSingleEvent(generateProductClickedData(productTrackingData));
    if (listingOpenedFrom) {
      pushEvent(PRODUCT_CARD_ACTION, { clickInteraction: listingOpenedFrom });
    }
  };
  const { disablePriveProduct } = product || {};

  return (
    <ActionWrapper
      isAnalyticsTrackingEnabled
      productTrackingData={productTrackingData}
      hasInteracted={hasInteracted}
      setInteraction={setInteraction}
      enableSecondaryTagTracking
    >
      {({
        handleNotifyMe,
        handleAddToCart,
        handleWishlist,
        handleSliderAction,
        openNotifyMe,
        handleVariantClick,
      }: ProductRenderProps) => (
        <ProductCard
          size={size}
          customClass={customClass}
          pageLocation={pageLocation}
          imageUrl={optimizedImage}
          product={product}
          showProFlag={showProTag}
          lazyLoadImage={positionInList > LAZYLOAD_COUNT}
          positionInList={positionInList}
          user={user}
          variants={variants}
          handleSlider={handleSliderAction}
          sendNotifyMe={handleNotifyMe}
          fetchVariants={fetchVariantsAction}
          isRegisteredViaMobile={isRegisteredViaMobile}
          redirectAuthPage={redirectAuthPage}
          addToCartCallback={handleAddToCart}
          wishlistCallback={handleWishlist}
          defaultColor={shouldShowDefaultColor}
          plpPriceReveal={plpPriceReveal}
          onIntersect={onIntersect}
          isServer={__SERVER__}
          handleProductCardClick={handleProductClick}
          handleVariantCallback={handleVariantClick}
          openNotifyMeCallback={openNotifyMe}
          listingPriceReveal={listingPriceReveal}
          journeyData={journeyData}
          isDifferential={isDifferentialPricingEnabled}
          isDisabled={disablePriveProduct && disableCta}
          tagsOnListingEnabled={tagsOnListingEnabled}
          navParams={{
            siteNavigation,
            siteSubNavigation,
          }}
          isNavParamsEnabled={isNavParamsEnabled}
          tagsV2Enabled={tagsV2Enabled}
        />
      )}
    </ActionWrapper>
  );
};

export default Product;
